import NoSsr from "@mui/material/NoSsr";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useItemTooltip, useMouseTracker } from "@mui/x-charts/ChartsTooltip";
import { Box } from "@mui/system";
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { generateVirtualElement } from "../../../../../ResuableChartTooltip/generateVirtualElement";

interface CustomTooltipProps {
  activityNotesDataGlobal?: any[];
  selectedTimeUnit?: { label: string };
  dataset?: any[];
  appendPercentage?: boolean;
}

export function CustomTooltip({
  activityNotesDataGlobal = [],
  selectedTimeUnit,
  dataset = [],
  appendPercentage,
}: CustomTooltipProps) {
  const rawTooltipData = useItemTooltip();
  const mousePosition = useMouseTracker();

  const [tooltipHovered, setTooltipHovered] = useState(false);
  const [persistedTooltipData, setPersistedTooltipData] = useState<any>(null);
  const [closeTimeout, setCloseTimeout] = useState<any>(null);

  // Persist tooltip data if it changes and not hovering
  useEffect(() => {
    if (rawTooltipData && !tooltipHovered) {
      setPersistedTooltipData(rawTooltipData);
    }

    // If tooltip disappears and not hovering, set delayed close
    if (!rawTooltipData && !tooltipHovered) {
      const timeout = setTimeout(() => {
        setPersistedTooltipData(null);
      }, 150); // Allow 150ms to hover the tooltip
      setCloseTimeout(timeout);
    }

    return () => {
      if (closeTimeout) clearTimeout(closeTimeout);
    };
    // eslint-disable-next-line
  }, [rawTooltipData, tooltipHovered]);

  const tooltipData = tooltipHovered ? persistedTooltipData : rawTooltipData;

  const notesByDate = useMemo(() => {
    if (!Array.isArray(activityNotesDataGlobal)) return {};
    return activityNotesDataGlobal.reduce((acc: any, note: any) => {
      if (!note.activity_date) return acc;
      const date = new Date(note.activity_date);
      const formattedDate =
        selectedTimeUnit?.label === "month"
          ? format(date, "MMMM yyyy")
          : format(date, "dd-MM-yyyy");

      if (!acc[formattedDate]) acc[formattedDate] = [];
      acc[formattedDate].push(note);
      return acc;
    }, {});
  }, [activityNotesDataGlobal, selectedTimeUnit]);

  if (!tooltipData || !mousePosition) return null;

  const isMousePointer = mousePosition?.pointerType === "mouse";
  const yOffset = isMousePointer ? 0 : 40 - mousePosition.height;

  const currentDataPoint =
    tooltipData.identifier?.dataIndex !== undefined
      ? dataset[tooltipData.identifier.dataIndex]
      : null;

  const currentDate = currentDataPoint?.x || null;
  const currentNotes = currentDate ? notesByDate[currentDate] || [] : [];

  const isNotesBar =
    tooltipData.identifier?.seriesId === 1 ||
    (tooltipData.formattedValue &&
      parseInt(tooltipData.formattedValue) > 0 &&
      currentNotes.length > 0);

  return (
    <NoSsr>
      <Popper
        sx={{ pointerEvents: "none", zIndex: (theme) => theme.zIndex.modal }}
        open={!!tooltipData}
        placement={isMousePointer ? "bottom" : "top"}
        anchorEl={generateVirtualElement(mousePosition)}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, yOffset],
            },
          },
        ]}
      >
        <Paper
          elevation={0}
          onMouseEnter={() => {
            setTooltipHovered(true);
            if (closeTimeout) clearTimeout(closeTimeout);
          }}
          onMouseLeave={() => {
            setTooltipHovered(false);
          }}
          sx={{
            pointerEvents: "auto",
            m: 1,
            p: 1.5,
            border: "solid",
            borderWidth: 0,
            borderColor: "divider",
            boxShadow: "0px 0px 3px #00000073",
            position: "relative",
            maxWidth: isNotesBar ? 400 : 200,
            maxHeight: 250,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#a0a0a0",
            },
            scrollbarWidth: "thin", // Firefox
            scrollbarColor: "#c1c1c1 transparent",
          }}
        >
          {currentNotes.length > 0 &&
          (isNotesBar ||
            tooltipData.formattedValue === currentNotes.length.toString()) ? (
            <Stack spacing={1}>
              <Typography fontWeight="medium" fontSize={14} color="secondary">
                Activity Notes ({currentNotes.length})
              </Typography>
              <Typography
                fontWeight="light"
                fontSize={12}
                color="textSecondary"
              >
                {currentDate}
              </Typography>
              <Box>
                {currentNotes.map((note: any, index: number) => (
                  <Box
                    key={note.shared_uid || index}
                    sx={{
                      mb: 1.5,
                      pb: 1,
                      borderBottom:
                        index < currentNotes.length - 1
                          ? "1px solid #e0e0e0"
                          : "none",
                    }}
                  >
                    <Typography
                      fontWeight="medium"
                      fontSize={12}
                      color="secondary.main"
                    >
                      {note.note_type}
                    </Typography>
                    <Typography fontSize={11} sx={{ mt: 0.5 }}>
                      {note.note}
                    </Typography>
                    {Array.isArray(note?.labels) && note.labels.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {note.labels.map(
                          (label: string, labelIndex: number) => (
                            <Typography
                              key={labelIndex}
                              fontSize={10}
                              sx={{
                                display: "inline-block",
                                bgcolor: "grey.200",
                                color: "grey.700",
                                px: 0.5,
                                py: 0.25,
                                borderRadius: 0.5,
                                mr: 0.5,
                              }}
                            >
                              {label}
                            </Typography>
                          )
                        )}
                      </Box>
                    )}
                    <Typography
                      fontSize={10}
                      color="textSecondary"
                      sx={{ mt: 0.5 }}
                    >
                      By: {note.created_by}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center">
              <Typography fontWeight="light" fontSize={18}>
                {tooltipData.formattedValue}
                {appendPercentage ? "%" : ""}
              </Typography>
            </Stack>
          )}

          <Box
            style={{
              position: "absolute",
              transform: "rotate(90deg)",
              top: "-10px",
              right: "45%",
              zIndex: "1",
              width: 0,
              height: 0,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderRight: "8px solid #fff",
            }}
          />
        </Paper>
      </Popper>
    </NoSsr>
  );
}
